<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { createConsumer } from '@rails/actioncable';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import PagesList from './components/PagesList.vue';
import PageConfigPanel from './components/PageConfigPanel.vue';
import WidgetSettingsPanel from './components/WidgetSettingsPanel.vue';
import BookingWidgetPreview from './components/BookingWidgetPreview.vue';
import BookingWidgetsAPI from 'dashboard/api/bookingWidgets';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { accountId } = useAccount();

const loading = ref(true);
const softSwitching = ref(false);
const saving = ref(false);
const fetchingSslStatus = ref(false);
const funnelLoading = ref(false);
const funnelSummary = ref(null);
const bookingWidget = ref(null);
const settingsSelected = ref(false);
const selectedPageId = ref(null);
const skipNextIdWatch = ref(false);
/** Narrow layout: one pane at a time — list → edit → preview. */
const isNarrowLayout = ref(false);
const mobilePane = ref('list'); // 'list' | 'edit' | 'preview'
let cableSubscription = null;
let skipCableReloadUntil = 0;
let narrowMq = null;

const syncNarrowLayout = () => {
  const next = !!narrowMq?.matches;
  isNarrowLayout.value = next;
  if (!next) mobilePane.value = 'list';
};

const isNew = computed(() => route.name === 'settings_booking_widgets_new');
const isPrimary = computed(() => bookingWidget.value?.is_primary !== false);
const structureLocked = computed(() => !isPrimary.value);

const pages = computed(() => bookingWidget.value?.booking_flow?.pages || []);

const selectedPage = computed(() =>
  pages.value.find(p => p.id === selectedPageId.value) || null
);

const spaces = computed(() => store.getters['spaces/getSpaces'] || []);
const experiences = computed(
  () => store.getters['planningExperiences/getRecords'] || []
);
const pricingItems = computed(
  () => store.getters['pricingItems/getRecords'] || []
);
const pricingPlans = computed(
  () => store.getters['pricingPlans/getRecords'] || []
);

const siblings = computed(() => bookingWidget.value?.siblings || []);

const funnelRootId = computed(() => {
  const primary = siblings.value.find(sibling => sibling.is_primary);
  return primary?.id || bookingWidget.value?.id || null;
});

/** Stable preview iframe token (primary) so locale switches don't reload the iframe. */
const previewWebsiteToken = computed(() => {
  const primary = siblings.value.find(sibling => sibling.is_primary);
  return (
    primary?.website_token ||
    bookingWidget.value?.website_token ||
    ''
  );
});

const widgetPublicUrl = computed(() => {
  const url = bookingWidget.value?.embed_url || '';
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${window.location.origin}${url}`;
});

const sectionHash = computed(() => {
  if (settingsSelected.value) return '#settings';
  if (selectedPageId.value) return `#${selectedPageId.value}`;
  return '';
});

const applyHashSelection = ({ openEdit = false } = {}) => {
  const hash = (route.hash || '').replace(/^#/, '');
  if (!hash) return false;
  if (hash === 'settings') {
    settingsSelected.value = true;
    if (openEdit && isNarrowLayout.value) mobilePane.value = 'edit';
    return true;
  }
  if (pages.value.some(page => page.id === hash)) {
    settingsSelected.value = false;
    selectedPageId.value = hash;
    if (openEdit && isNarrowLayout.value) mobilePane.value = 'edit';
    return true;
  }
  return false;
};

const syncSectionHash = () => {
  const next = sectionHash.value;
  if ((route.hash || '') === next) return;
  router.replace({ hash: next || undefined });
};

const ensureSelection = ({ keepPage = false } = {}) => {
  if (applyHashSelection()) return;

  if (keepPage && selectedPageId.value) {
    const stillThere = pages.value.some(p => p.id === selectedPageId.value);
    if (stillThere) return;
  }
  if (!selectedPageId.value && pages.value.length) {
    selectedPageId.value =
      pages.value.find(p => p.enabled !== false)?.id || pages.value[0].id;
  } else if (
    selectedPageId.value &&
    !pages.value.some(p => p.id === selectedPageId.value)
  ) {
    selectedPageId.value =
      pages.value.find(p => p.enabled !== false)?.id ||
      pages.value[0]?.id ||
      null;
  }
};

const CORE_PAGE_TYPES = ['filter', 'offering', 'event_information'];
const TERMINAL_PAGE_TYPES = ['review_submit', 'confirmation'];

/** Empty curated lists mean "all" — materialize so the multi-select shows every chip. */
const seedCuratedDefaults = () => {
  if (!isPrimary.value) return;
  const flow = bookingWidget.value?.booking_flow;
  if (!flow?.pages) return;

  const allSpaceIds = spaces.value.map(space => String(space.id));
  const allExperienceIds = experiences.value.map(exp => String(exp.id));

  flow.pages = flow.pages.map(page => {
    if (page.type !== 'offering') return page;
    const config = { ...(page.config || {}) };
    let changed = false;
    if (!(config.spaceIds || []).length && allSpaceIds.length) {
      config.spaceIds = [...allSpaceIds];
      changed = true;
    }
    if (!(config.experienceIds || []).length && allExperienceIds.length) {
      config.experienceIds = [...allExperienceIds];
      changed = true;
    }
    return changed ? { ...page, config } : page;
  });
};

const loadFunnel = async () => {
  const id = funnelRootId.value;
  if (!id) {
    funnelSummary.value = null;
    return;
  }
  funnelLoading.value = true;
  try {
    const { data } = await BookingWidgetsAPI.getFunnel(id);
    funnelSummary.value = data?.summary || null;
  } catch {
    funnelSummary.value = null;
  } finally {
    funnelLoading.value = false;
  }
};

const isCoreFlowPage = page => CORE_PAGE_TYPES.includes(page?.type);
const isTerminalFlowPage = page => TERMINAL_PAGE_TYPES.includes(page?.type);

const isPinnedFlowPage = page =>
  page?.movable === false ||
  isCoreFlowPage(page) ||
  isTerminalFlowPage(page);

/** Keep Filter → Offering → Event Information contiguous; terminals last. */
const normalizeFixedPageOrder = pages => {
  const list = Array.isArray(pages) ? [...pages] : [];
  const byType = type => list.find(p => p.type === type);
  const core = CORE_PAGE_TYPES.map(byType).filter(Boolean);
  const terminals = TERMINAL_PAGE_TYPES.map(byType).filter(Boolean);
  const coreIds = new Set(core.map(p => p.id));
  const terminalIds = new Set(terminals.map(p => p.id));
  const firstCoreIdx = list.findIndex(p => coreIds.has(p.id));

  const before = [];
  const after = [];
  list.forEach((page, index) => {
    if (coreIds.has(page.id) || terminalIds.has(page.id)) return;
    if (firstCoreIdx >= 0 && index < firstCoreIdx) before.push(page);
    else after.push(page);
  });

  return [...before, ...core, ...after, ...terminals];
};

const applyWidget = (widget, { soft = false } = {}) => {
  const cloned = structuredClone(widget);
  if (cloned?.booking_flow?.pages) {
    cloned.booking_flow.pages = normalizeFixedPageOrder(
      cloned.booking_flow.pages
    );
  }
  bookingWidget.value = cloned;
  seedCuratedDefaults();
  ensureSelection({ keepPage: soft });
  if (!soft) loadFunnel();
};

const loadWidget = async ({ soft = false, id = null } = {}) => {
  if (soft) softSwitching.value = true;
  else loading.value = true;
  try {
    if (isNew.value) {
      const created = await store.dispatch('bookingWidgets/create', {
        name: t('BOOKING_WIDGETS.DEFAULT_NAME'),
      });
      if (created?.id) {
        skipNextIdWatch.value = true;
        await router.replace({
          name: 'settings_booking_widgets_show',
          params: { bookingWidgetId: created.id },
        });
        applyWidget(created, { soft: false });
      }
      return;
    }
    const widgetId = id || route.params.bookingWidgetId;
    const widget = await store.dispatch('bookingWidgets/show', widgetId);
    applyWidget(widget, { soft });
  } catch (error) {
    useAlert(error.message || t('BOOKING_WIDGETS.LOAD_ERROR'));
  } finally {
    loading.value = false;
    softSwitching.value = false;
  }
};

const unsubscribeBookingWidgetCable = () => {
  if (cableSubscription) {
    cableSubscription.unsubscribe();
    cableSubscription = null;
  }
};

const subscribeBookingWidgetCable = () => {
  unsubscribeBookingWidgetCable();
  const rootId =
    bookingWidget.value?.parent_booking_widget_id || bookingWidget.value?.id;
  if (!rootId || !accountId.value) return;

  const consumer = createConsumer();
  cableSubscription = consumer.subscriptions.create(
    {
      channel: 'BookingWidgetChannel',
      booking_widget_id: rootId,
      account_id: Number(accountId.value),
    },
    {
      received(data) {
        if (data.event !== 'booking_widget.updated') return;
        if (Date.now() < skipCableReloadUntil) return;
        loadWidget({ soft: true });
      },
    }
  );
};

onMounted(async () => {
  if (typeof window !== 'undefined') {
    narrowMq = window.matchMedia('(max-width: 1023px)');
    syncNarrowLayout();
    narrowMq.addEventListener?.('change', syncNarrowLayout);
  }
  await Promise.all([
    store.dispatch('spaces/get'),
    store.dispatch('planningExperiences/get'),
    store.dispatch('pricingItems/get'),
    store.dispatch('pricingPlans/get'),
  ]);
  await loadWidget();
  // Deep links (#settings / #page) open the editor on narrow screens.
  applyHashSelection({ openEdit: true });
  subscribeBookingWidgetCable();
});

onUnmounted(() => {
  narrowMq?.removeEventListener?.('change', syncNarrowLayout);
  unsubscribeBookingWidgetCable();
});

watch(
  () => bookingWidget.value?.id,
  () => subscribeBookingWidgetCable()
);

watch(
  () => route.params.bookingWidgetId,
  (id, prevId) => {
    if (isNew.value || !id) return;
    if (skipNextIdWatch.value) {
      skipNextIdWatch.value = false;
      return;
    }
    if (String(id) === String(bookingWidget.value?.id)) return;
    const soft =
      !!bookingWidget.value && !!prevId && String(id) !== String(prevId);
    loadWidget({ soft, id });
  }
);

watch(
  () => route.hash,
  () => {
    if (!bookingWidget.value) return;
    applyHashSelection({ openEdit: isNarrowLayout.value });
  }
);

watch(sectionHash, () => {
  if (!bookingWidget.value || loading.value) return;
  syncSectionHash();
});

const selectSettings = () => {
  settingsSelected.value = true;
  if (isNarrowLayout.value && mobilePane.value === 'list') {
    mobilePane.value = 'edit';
  }
};

const selectPage = id => {
  settingsSelected.value = false;
  selectedPageId.value = id;
  // Only drill in from the page list — preview page sync must not leave preview.
  if (isNarrowLayout.value && mobilePane.value === 'list') {
    mobilePane.value = 'edit';
  }
};

const backToPageList = () => {
  mobilePane.value = 'list';
};

const openMobilePreview = () => {
  mobilePane.value = 'preview';
};

const backToEditor = () => {
  mobilePane.value = 'edit';
};

const mobileEditTitle = computed(() => {
  if (settingsSelected.value) return t('BOOKING_WIDGETS.SETTINGS');
  const page = selectedPage.value;
  if (!page) return t('BOOKING_WIDGETS.WIDGET_SETTINGS');
  return (
    page.label ||
    t(`BOOKING_WIDGETS.SIDEBAR.${page.type}`) ||
    page.type
  );
});

const showPagesList = computed(
  () => !isNarrowLayout.value || mobilePane.value === 'list'
);
const showEditorPane = computed(
  () => !isNarrowLayout.value || mobilePane.value === 'edit'
);
const showPreviewPane = computed(
  () => !isNarrowLayout.value || mobilePane.value === 'preview'
);

const togglePage = ({ id, enabled }) => {
  if (structureLocked.value) return;
  const flow = bookingWidget.value.booking_flow;
  flow.pages = flow.pages.map(page =>
    page.id === id
      ? {
          ...page,
          enabled: page.disableable === false ? true : enabled,
        }
      : page
  );
};

const reorderPages = ({ from, to }) => {
  if (structureLocked.value) return;
  const list = [...bookingWidget.value.booking_flow.pages];
  if (isPinnedFlowPage(list[from])) return;
  const [item] = list.splice(from, 1);
  list.splice(to, 0, item);
  bookingWidget.value.booking_flow.pages = normalizeFixedPageOrder(list);
};

const updatePage = page => {
  bookingWidget.value.booking_flow.pages =
    bookingWidget.value.booking_flow.pages.map(p =>
      p.id === page.id ? page : p
    );
};

const updateWidget = patch => {
  bookingWidget.value = { ...bookingWidget.value, ...patch };
};

const switchLocale = async sibling => {
  if (!sibling?.id || String(sibling.id) === String(bookingWidget.value?.id)) {
    return;
  }
  if (softSwitching.value || loading.value) return;

  softSwitching.value = true;
  try {
    const widget = await store.dispatch('bookingWidgets/show', sibling.id);
    applyWidget(widget, { soft: true });
    skipNextIdWatch.value = true;
    await router.replace({
      name: 'settings_booking_widgets_show',
      params: {
        accountId: accountId.value,
        bookingWidgetId: sibling.id,
      },
      hash: sectionHash.value || undefined,
    });
  } catch (error) {
    useAlert(error.message || t('BOOKING_WIDGETS.LOAD_ERROR'));
  } finally {
    softSwitching.value = false;
  }
};

const save = async () => {
  if (!bookingWidget.value?.id || saving.value) return;
  skipCableReloadUntil = Date.now() + 1500;
  saving.value = true;
  try {
    const payload = {
      id: bookingWidget.value.id,
      name: bookingWidget.value.name,
      booking_flow: bookingWidget.value.booking_flow,
    };
    if (isPrimary.value) {
      Object.assign(payload, {
        enabled: bookingWidget.value.enabled,
        pricing_plan_id: bookingWidget.value.pricing_plan_id,
        widget_color: bookingWidget.value.widget_color,
        widget_backgroundcolor: bookingWidget.value.widget_backgroundcolor,
        design_options: bookingWidget.value.design_options,
        analytics_settings: bookingWidget.value.analytics_settings,
        custom_domain: bookingWidget.value.custom_domain,
      });
    }
    const updated = await store.dispatch('bookingWidgets/update', payload);
    applyWidget(updated, { soft: true });
    useAlert(t('BOOKING_WIDGETS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('BOOKING_WIDGETS.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

const saveCustomDomain = async customDomain => {
  if (!bookingWidget.value?.id || !isPrimary.value) return;
  try {
    const updated = await store.dispatch('bookingWidgets/update', {
      id: bookingWidget.value.id,
      custom_domain: customDomain,
    });
    applyWidget(updated, { soft: true });
    useAlert(t('BOOKING_WIDGETS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('BOOKING_WIDGETS.SAVE_ERROR'));
  }
};

const refreshSslStatus = async () => {
  if (!bookingWidget.value?.id || fetchingSslStatus.value) return;
  fetchingSslStatus.value = true;
  try {
    const data = await store.dispatch(
      'bookingWidgets/sslStatus',
      bookingWidget.value.id
    );
    if (data) {
      bookingWidget.value = {
        ...bookingWidget.value,
        ssl_settings: {
          status: data.status,
          verification_errors: data.verification_errors,
        },
      };
    }
  } finally {
    fetchingSslStatus.value = false;
  }
};

const sendDomainInstructions = async email => {
  if (!bookingWidget.value?.id) return;
  try {
    await store.dispatch('bookingWidgets/sendDomainInstructions', {
      id: bookingWidget.value.id,
      email,
    });
    useAlert(t('BOOKING_WIDGETS.CUSTOM_DOMAIN.DNS.SENT'));
  } catch (error) {
    useAlert(error.message || t('BOOKING_WIDGETS.SAVE_ERROR'));
  }
};
</script>

<template>
  <div
    class="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden xl:flex-row"
  >
    <div
      v-if="loading || !bookingWidget"
      class="flex min-h-0 flex-1 items-center justify-center text-n-slate-11"
    >
      Loading…
    </div>

    <template v-else>
      <PagesList
        v-show="showPagesList"
        :class="
          isNarrowLayout
            ? '!h-full !w-full !border-r-0'
            : ''
        "
        :pages="pages"
        :selected-page-id="selectedPageId"
        :settings-selected="settingsSelected"
        :structure-locked="structureLocked"
        :siblings="siblings"
        :current-locale="bookingWidget.locale"
        :soft-switching="softSwitching"
        :funnel-summary="funnelSummary"
        :funnel-loading="funnelLoading"
        @select-settings="selectSettings"
        @select-page="selectPage"
        @toggle-page="togglePage"
        @reorder="reorderPages"
        @change-locale="switchLocale"
      />

      <div
        v-show="showEditorPane || showPreviewPane"
        class="flex min-h-0 min-w-0 flex-1 flex-col transition-opacity duration-150 lg:flex-row"
        :class="{ 'opacity-70': softSwitching }"
      >
        <div
          v-show="showEditorPane"
          class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden lg:max-w-xl xl:max-w-none"
        >
          <div
            v-if="isNarrowLayout"
            class="flex h-14 shrink-0 items-center gap-2 border-b border-n-weak px-3"
          >
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1"
              @click="backToPageList"
            >
              <span class="i-lucide-chevron-left size-4 shrink-0" />
              {{ t('BOOKING_WIDGETS.BACK') }}
            </button>
            <h3
              class="min-w-0 flex-1 truncate text-sm font-semibold text-n-slate-12"
            >
              {{ mobileEditTitle }}
            </h3>
            <button
              type="button"
              class="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-n-brand px-3 py-1.5 text-sm font-semibold text-white"
              @click="openMobilePreview"
            >
              <span class="i-lucide-eye size-4 shrink-0" />
              {{ t('BOOKING_WIDGETS.PREVIEW') }}
            </button>
          </div>

          <WidgetSettingsPanel
            v-if="settingsSelected"
            :widget="bookingWidget"
            :pricing-plans="pricingPlans"
            :structure-locked="structureLocked"
            :is-fetching-ssl-status="fetchingSslStatus"
            @update="updateWidget"
            @save="save"
            @save-custom-domain="saveCustomDomain"
            @refresh-ssl-status="refreshSslStatus"
            @send-domain-instructions="sendDomainInstructions"
          />
          <PageConfigPanel
            v-else
            :page="selectedPage"
            :pages="pages"
            :widget-name="bookingWidget.name"
            :spaces="spaces"
            :experiences="experiences"
            :pricing-items="pricingItems"
            :text-only="structureLocked"
            @update-page="updatePage"
            @save="save"
          />
        </div>

        <div
          v-show="showPreviewPane"
          class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
          :class="isNarrowLayout ? 'w-full' : ''"
        >
          <div
            v-if="isNarrowLayout"
            class="flex h-14 shrink-0 items-center gap-2 border-b border-n-weak bg-slate-950 px-3"
          >
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-medium text-slate-200 hover:bg-slate-800"
              @click="backToEditor"
            >
              <span class="i-lucide-chevron-left size-4 shrink-0" />
              {{ t('BOOKING_WIDGETS.BACK') }}
            </button>
            <h3
              class="min-w-0 flex-1 truncate text-sm font-semibold text-white"
            >
              {{ t('BOOKING_WIDGETS.PREVIEW') }}
            </h3>
          </div>

          <BookingWidgetPreview
            class="min-h-0 flex-1"
            :class="
              isNarrowLayout
                ? '!w-full !border-l-0'
                : ''
            "
            :website-token="previewWebsiteToken"
            :widget-url="widgetPublicUrl"
            :booking-flow="bookingWidget.booking_flow"
            :widget-color="
              bookingWidget.resolved_widget_color || bookingWidget.widget_color
            "
            :background-color="
              bookingWidget.resolved_widget_backgroundcolor ||
              bookingWidget.widget_backgroundcolor
            "
            :design-options="{
              ...(bookingWidget.resolved_design_options || {}),
              ...(bookingWidget.design_options || {}),
            }"
            :selected-page-id="settingsSelected ? null : selectedPageId"
            :embedded="isNarrowLayout"
            @select-page="selectPage"
          />
        </div>
      </div>
    </template>
  </div>
</template>
